import React, { useState, useMemo, useEffect, useRef } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import ImageHotspotPlayer from './ImageHotspotPlayer';
import { getImageUrl } from '../../../services/storageService';

// Score Result Modal shown after each game completes
const ScoreModal = ({ scoreData, gameType, onContinue }) => {
  if (!scoreData) return null;

  const { correct, total, score } = scoreData;
  const percentage = total > 0 ? Math.round((correct / total) * 100) : (score === 1 ? 100 : 0);
  const isPerfect = percentage === 100;
  const isGood = percentage >= 70;

  const getStarCount = () => {
    if (isPerfect) return 3;
    if (isGood) return 2;
    return 1;
  };

  const getMessage = () => {
    if (isPerfect) return 'Perfect score! Outstanding work!';
    if (isGood) return 'Great job! Keep it up!';
    return 'Good effort! Keep practicing!';
  };

  const stars = getStarCount();

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-sm w-full p-6 text-center animate-in fade-in zoom-in duration-300">
        <div className={`inline-flex items-center justify-center w-16 h-16 rounded-full mb-4 ${
          isPerfect ? 'bg-yellow-100' : isGood ? 'bg-green-100' : 'bg-blue-100'
        }`}>
          <Icon
            name={isPerfect ? 'Trophy' : isGood ? 'CheckCircle2' : 'Star'}
            size={32}
            className={isPerfect ? 'text-yellow-500' : isGood ? 'text-green-500' : 'text-blue-500'}
          />
        </div>

        <h3 className="text-xl font-bold text-foreground mb-1">Game Complete!</h3>
        <p className="text-sm text-muted-foreground mb-4">{getMessage()}</p>

        {/* Score display */}
        <div className={`rounded-xl p-4 mb-4 ${
          isPerfect ? 'bg-yellow-50 border border-yellow-200' : isGood ?'bg-green-50 border border-green-200': 'bg-blue-50 border border-blue-200'
        }`}>
          <div className="text-3xl font-bold text-foreground mb-1">
            {correct !== undefined ? `${correct}/${total}` : (score === 1 ? '1/1' : '0/1')}
          </div>
          <div className={`text-lg font-semibold ${
            isPerfect ? 'text-yellow-600' : isGood ? 'text-green-600' : 'text-blue-600'
          }`}>
            {percentage}%
          </div>
        </div>

        {/* Stars earned for this game */}
        <div className="flex items-center justify-center space-x-1 mb-5">
          {[1, 2, 3]?.map(s => (
            <Icon
              key={s}
              name="Star"
              size={24}
              className={s <= stars ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'}
            />
          ))}
        </div>

        <Button onClick={onContinue} iconName="ArrowRight" iconPosition="right" fullWidth>
          Continue
        </Button>
      </div>
    </div>
  );
};

// Helper: render a game card value (text or image)
const GameCardContent = ({ value, imageUrl, altText, className = '' }) => {
  if (imageUrl) {
    return (
      <div className={`flex flex-col items-center justify-center gap-1 ${className}`}>
        <img
          src={imageUrl}
          alt={altText || value || 'Game card image'}
          className="w-full h-full object-cover rounded"
          style={{ minWidth: 60, minHeight: 60, maxHeight: 100 }}
        />
        {value && (
          <span className="text-xs text-center text-muted-foreground leading-tight line-clamp-2 w-full">{value}</span>
        )}
      </div>
    );
  }
  return <span className={`text-xs sm:text-sm text-center break-words ${className}`}>{value}</span>;
};

const GameRenderer = ({ content, onComplete, isCompleted }) => {
  const contentData = content?.content_data || {};

  // Try to extract gameType from multiple possible locations
  const gameType = contentData?.gameType || contentData?.game_type;

  // Try to extract gameData
  let gameData = contentData?.gameData || contentData?.game_data;

  // If gameData is not found, check if the game properties are directly in contentData
  if (!gameData || Object.keys(gameData || {})?.length === 0) {
    gameData = {
      pairs: contentData?.pairs,
      words: contentData?.words,
      gridSize: contentData?.gridSize || contentData?.grid_size,
      items: contentData?.items,
      statement: contentData?.statement,
      answer: contentData?.answer,
      blanks: contentData?.blanks,
      categories: contentData?.categories,
      hotspotImage: contentData?.hotspotImage,
      hotspots: contentData?.hotspots
    };
  }

  const title = contentData?.title || 'Interactive Game';
  const description = contentData?.description || '';

  // Resolved image URLs: maps filePath -> signed URL
  const [resolvedImageUrls, setResolvedImageUrls] = useState({});
  const [imagesLoading, setImagesLoading] = useState(false);

  // Helper: check if a string is a file path (not already a full URL)
  const isFilePath = (str) => {
    if (!str) return false;
    return !str?.startsWith('http://') && !str?.startsWith('https://');
  };

  // Stable list of image paths to resolve (avoids re-running effect on every render)
  const imagePathsToResolve = useMemo(() => {
    const paths = new Set();

    if (gameType === 'memory-match' && gameData?.pairs) {
      gameData?.pairs?.forEach(p => {
        if (p?.termImage && isFilePath(p?.termImage)) paths?.add(p?.termImage);
        if (p?.definitionImage && isFilePath(p?.definitionImage)) paths?.add(p?.definitionImage);
      });
    }

    if (gameType === 'drag-drop' && gameData?.items) {
      gameData?.items?.forEach(item => {
        if (item?.itemImage && isFilePath(item?.itemImage)) paths?.add(item?.itemImage);
      });
    }

    if (gameType === 'sorting' && gameData?.categories) {
      gameData?.categories?.forEach(cat => {
        (cat?.items || [])?.forEach(it => {
          if (it && typeof it === 'object' && it?.itemImage && isFilePath(it?.itemImage)) {
            paths?.add(it?.itemImage);
          }
        });
      });
    }

    return [...paths];
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gameType, content]);

  // Collect all image paths that need resolution
  useEffect(() => {
    if (imagePathsToResolve?.length === 0) return;

    setImagesLoading(true);

    const resolveAll = async () => {
      const newUrls = {};
      await Promise.all(
        imagePathsToResolve?.map(async (path) => {
          const { url, error } = await getImageUrl(path, 86400);
          if (!error && url) {
            newUrls[path] = url;
          }
        })
      );
      setResolvedImageUrls(prev => ({ ...prev, ...newUrls }));

      // Preload all resolved images into browser cache
      const preloadPromises = Object.values(newUrls)?.map(url => {
        return new Promise((resolve) => {
          const img = new Image();
          img.onload = resolve;
          img.onerror = resolve; // resolve even on error so we don't block forever
          img.src = url;
        });
      });
      await Promise.all(preloadPromises);

      setImagesLoading(false);
    };

    resolveAll();
  }, [imagePathsToResolve]);

  // Helper: get the display URL for an image field (resolves file path or returns direct URL)
  const getDisplayUrl = (imageField) => {
    if (!imageField) return null;
    if (isFilePath(imageField)) return resolvedImageUrls?.[imageField] || null;
    return imageField;
  };

  const [gameState, setGameState] = useState({
    selectedAnswers: {},
    draggedItems: [],
    userInput: {},
    categoryAssignments: {},
    flippedCards: [],
    matchedPairs: [],
    foundWords: [],
    selectedCells: [],
    currentSelection: [],
    isComplete: false
  });

  const [draggedItem, setDraggedItem] = useState(null);
  const [dragDropOrder, setDragDropOrder] = useState(() => {
    if (gameType === 'drag-drop' && gameData?.items) {
      return [...(gameData?.items || [])]?.sort(() => Math.random() - 0.5);
    }
    return [];
  });
  const [sortingDraggedItem, setSortingDraggedItem] = useState(null);
  const [dragOverCategory, setDragOverCategory] = useState(null);

  // Score modal state
  const [pendingScore, setPendingScore] = useState(null);

  // Helper: show score modal then call onComplete
  const showScoreAndComplete = (scoreData) => {
    setPendingScore(scoreData);
  };

  const handleScoreModalContinue = () => {
    const score = pendingScore;
    setPendingScore(null);
    onComplete?.(score);
  };

  // Generate word search grid
  const wordSearchGrid = useMemo(() => {
    if (gameType !== 'word-search' || !gameData?.words) return null;

    const words = gameData?.words || [];
    const gridSizeStr = gameData?.gridSize || '10x10';

    let rows, cols;
    if (gridSizeStr === 'custom' || gridSizeStr === 'Custom') {
      cols = parseInt(gameData?.customWidth) || 10;
      rows = parseInt(gameData?.customHeight) || 10;
    } else {
      [rows, cols] = gridSizeStr?.split('x')?.map(Number);
    }

    const grid = Array(rows)?.fill(null)?.map(() => Array(cols)?.fill(''));
    let placedWords = [];

    const canPlaceWord = (word, row, col, direction) => {
      const len = word?.length;
      if (direction === 'horizontal') {
        if (col + len > cols) return false;
        for (let i = 0; i < len; i++) {
          if (grid?.[row]?.[col + i] !== '' && grid?.[row]?.[col + i] !== word?.[i]) return false;
        }
        return true;
      } else if (direction === 'vertical') {
        if (row + len > rows) return false;
        for (let i = 0; i < len; i++) {
          if (grid?.[row + i]?.[col] !== '' && grid?.[row + i]?.[col] !== word?.[i]) return false;
        }
        return true;
      } else if (direction === 'diagonal') {
        if (row + len > rows || col + len > cols) return false;
        for (let i = 0; i < len; i++) {
          if (grid?.[row + i]?.[col + i] !== '' && grid?.[row + i]?.[col + i] !== word?.[i]) return false;
        }
        return true;
      }
      return false;
    };

    const placeWord = (word, row, col, direction) => {
      const positions = [];
      for (let i = 0; i < word?.length; i++) {
        if (direction === 'horizontal') {
          grid[row][col + i] = word?.[i];
          positions?.push({ row, col: col + i });
        } else if (direction === 'vertical') {
          grid[row + i][col] = word?.[i];
          positions?.push({ row: row + i, col });
        } else if (direction === 'diagonal') {
          grid[row + i][col + i] = word?.[i];
          positions?.push({ row: row + i, col: col + i });
        }
      }
      placedWords?.push({ word, positions });
    };

    words?.forEach(word => {
      const upperWord = word?.toUpperCase();
      const directions = ['horizontal', 'vertical', 'diagonal'];
      let placed = false;
      let attempts = 0;

      while (!placed && attempts < 100) {
        const direction = directions?.[Math.floor(Math.random() * directions?.length)];
        const row = Math.floor(Math.random() * rows);
        const col = Math.floor(Math.random() * cols);

        if (canPlaceWord(upperWord, row, col, direction)) {
          placeWord(upperWord, row, col, direction);
          placed = true;
        }
        attempts++;
      }
    });

    const letters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
    for (let i = 0; i < rows; i++) {
      for (let j = 0; j < cols; j++) {
        if (grid?.[i]?.[j] === '') {
          grid[i][j] = letters?.[Math.floor(Math.random() * letters?.length)];
        }
      }
    }

    return { grid, placedWords };
  }, [gameType, gameData?.words, gameData?.gridSize]);

  // Shuffle cards for memory match (memoized)
  const shuffledCards = useMemo(() => {
    if (gameType !== 'memory-match' || !gameData?.pairs) return [];
    const pairs = gameData?.pairs || [];
    const allCards = [
      ...pairs?.map((p, i) => ({
        type: 'term',
        value: p?.term,
        imagePath: p?.termType === 'image' ? p?.termImage : null,
        pairId: i
      })),
      ...pairs?.map((p, i) => ({
        type: 'definition',
        value: p?.definition,
        imagePath: p?.definitionType === 'image' ? p?.definitionImage : null,
        pairId: i
      }))
    ];
    return allCards?.sort(() => Math.random() - 0.5);
  }, [gameType, gameData?.pairs]);

  const handleWordSearchCellClick = (row, col) => {
    const cellKey = `${row}-${col}`;
    const newSelection = [...(gameState?.currentSelection || [])];

    if (newSelection?.some(cell => cell?.key === cellKey)) {
      const filtered = newSelection?.filter(cell => cell?.key !== cellKey);
      setGameState(prev => ({ ...prev, currentSelection: filtered }));
    } else {
      newSelection?.push({ row, col, key: cellKey, letter: wordSearchGrid?.grid?.[row]?.[col] });
      setGameState(prev => ({ ...prev, currentSelection: newSelection }));
    }
  };

  const checkWordSearchSelection = () => {
    const selectedWord = gameState?.currentSelection?.map(cell => cell?.letter)?.join('');
    const words = gameData?.words?.map(w => w?.toUpperCase()) || [];

    if (words?.includes(selectedWord) && !gameState?.foundWords?.includes(selectedWord)) {
      const newFoundWords = [...(gameState?.foundWords || []), selectedWord];
      setGameState(prev => ({
        ...prev,
        foundWords: newFoundWords,
        selectedCells: [...(prev?.selectedCells || []), ...(prev?.currentSelection?.map(c => c?.key) || [])],
        currentSelection: []
      }));

      if (newFoundWords?.length === gameData?.words?.length) {
        const total = gameData?.words?.length || 1;
        setTimeout(() => showScoreAndComplete({ correct: total, total, score: 1, gameType: 'word-search' }), 800);
      }
    } else {
      setGameState(prev => ({ ...prev, currentSelection: [] }));
    }
  };

  const checkMemoryMatch = (index1, index2) => {
    if (shuffledCards?.[index1]?.pairId === shuffledCards?.[index2]?.pairId) {
      const newMatchedPairs = [...(gameState?.matchedPairs || []), shuffledCards?.[index1]?.pairId];
      setGameState(prev => ({
        ...prev,
        matchedPairs: newMatchedPairs,
        flippedCards: []
      }));

      if (newMatchedPairs?.length === gameData?.pairs?.length) {
        const total = gameData?.pairs?.length || 1;
        setTimeout(() => showScoreAndComplete({ correct: total, total, score: 1, gameType: 'memory-match' }), 800);
      }
    } else {
      setTimeout(() => {
        setGameState(prev => ({ ...prev, flippedCards: [] }));
      }, 1000);
    }
  };

  const handleCardFlip = (index) => {
    if ((gameState?.flippedCards?.length || 0) === 2 || gameState?.flippedCards?.includes(index)) return;

    const newFlipped = [...(gameState?.flippedCards || []), index];
    setGameState(prev => ({ ...prev, flippedCards: newFlipped }));

    if (newFlipped?.length === 2) {
      checkMemoryMatch(newFlipped?.[0], newFlipped?.[1]);
    }
  };

  const handleDragStart = (e, index) => {
    setDraggedItem(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e) => {
    e?.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e, dropIndex) => {
    e?.preventDefault();
    if (draggedItem === null) return;

    const newOrder = [...dragDropOrder];
    const draggedElement = newOrder?.[draggedItem];
    newOrder?.splice(draggedItem, 1);
    newOrder?.splice(dropIndex, 0, draggedElement);

    setDragDropOrder(newOrder);
    setDraggedItem(null);
  };

  const checkDragDropOrder = () => {
    const correct = dragDropOrder?.every((item, idx) => {
      // Handle object items with correctPosition or order property
      if (item !== null && typeof item === 'object') {
        const correctPos = item?.correctPosition !== undefined ? item?.correctPosition : item?.order;
        if (correctPos !== undefined) {
          return correctPos === idx + 1 || correctPos === idx;
        }
      }
      // Handle string items: compare against original gameData items order
      const originalItems = gameData?.items || [];
      const originalIndex = originalItems?.findIndex(orig => {
        if (orig !== null && typeof orig === 'object') return orig?.text === (item?.text || item);
        return orig === item;
      });
      return originalIndex === idx;
    });
    setGameState(prev => ({ ...prev, isComplete: correct, orderChecked: true }));
    if (correct) {
      setTimeout(() => showScoreAndComplete({ correct: 1, total: 1, score: 1, gameType: 'drag-drop' }), 800);
    }
  };

  const handleTrueFalseAnswer = (answer) => {
    const isCorrect = answer === gameData?.answer;
    setGameState(prev => ({ ...prev, selectedAnswers: { trueFalse: answer }, isComplete: isCorrect }));
    if (isCorrect) {
      setTimeout(() => showScoreAndComplete({ correct: 1, total: 1, score: 1, gameType: 'true-false' }), 800);
    }
  };

  const handleFillBlankInput = (index, value) => {
    setGameState(prev => ({
      ...prev,
      userInput: { ...(prev?.userInput || {}), [index]: value }
    }));
  };

  const checkFillBlanks = () => {
    const blanks = gameData?.blanks || [];
    let correctCount = blanks?.filter((blank, index) =>
      gameState?.userInput?.[index]?.toLowerCase()?.trim() === blank?.answer?.toLowerCase()?.trim()
    )?.length;
    const total = blanks?.length || 1;
    const allCorrect = correctCount === total;
    setGameState(prev => ({ ...prev, isComplete: allCorrect }));
    if (allCorrect) {
      setTimeout(() => showScoreAndComplete({ correct: correctCount, total, score: correctCount / total, gameType: 'fill-blanks' }), 800);
    }
  };

  const handleSortingDrop = (item, categoryIndex) => {
    setGameState(prev => ({
      ...prev,
      categoryAssignments: { ...(prev?.categoryAssignments || {}), [item]: categoryIndex }
    }));
  };

  const handleSortingDragStart = (e, item, fromCategory) => {
    setSortingDraggedItem({ item, fromCategory });
    e.dataTransfer.effectAllowed = 'move';
    e?.dataTransfer?.setData('text/plain', item);
  };

  const handleSortingDragOver = (e, catIndex) => {
    e?.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverCategory(catIndex);
  };

  const handleSortingDropOnCategory = (e, catIndex) => {
    e?.preventDefault();
    setDragOverCategory(null);
    if (!sortingDraggedItem) return;
    setGameState(prev => ({
      ...prev,
      categoryAssignments: { ...(prev?.categoryAssignments || {}), [sortingDraggedItem?.item]: catIndex }
    }));
    setSortingDraggedItem(null);
  };

  const handleSortingDragLeave = (e) => {
    if (!e?.currentTarget?.contains(e?.relatedTarget)) {
      setDragOverCategory(null);
    }
  };

  const handleSortingDropOnPool = (e) => {
    e?.preventDefault();
    setDragOverCategory(null);
    if (!sortingDraggedItem) return;
    setGameState(prev => {
      const newAssignments = { ...(prev?.categoryAssignments || {}) };
      delete newAssignments?.[sortingDraggedItem?.item];
      return { ...prev, categoryAssignments: newAssignments };
    });
    setSortingDraggedItem(null);
  };

  const handleSortingDragEnd = () => {
    setSortingDraggedItem(null);
    setDragOverCategory(null);
  };

  const checkSorting = () => {
    const categories = gameData?.categories || [];
    // Normalize items to { obj, key } pairs
    const allItemPairs = categories?.flatMap((cat, catIndex) =>
      (cat?.items || [])?.map(it => ({
        obj: it,
        key: typeof it === 'object' ? (it?.text || JSON.stringify(it)) : it,
        catIndex
      }))
    );
    const total = allItemPairs?.length || 1;

    // Check if all items have been assigned to a category
    const allAssigned = allItemPairs?.every(({ key }) =>
      gameState?.categoryAssignments?.[key] !== undefined &&
      gameState?.categoryAssignments?.[key] !== null
    );

    if (!allAssigned) {
      setGameState(prev => ({ ...prev, isComplete: false, notAllPlaced: true }));
      return;
    }

    let correctCount = 0;
    allItemPairs?.forEach(({ key, catIndex }) => {
      if (gameState?.categoryAssignments?.[key] === catIndex) {
        correctCount++;
      }
    });
    const allCorrect = correctCount === total;
    setGameState(prev => ({ ...prev, isComplete: allCorrect, notAllPlaced: false }));
    if (allCorrect) {
      setTimeout(() => showScoreAndComplete({ correct: correctCount, total, score: correctCount / total, gameType: 'sorting' }), 800);
    }
  };

  return (
    <div className="space-y-3 sm:space-y-4">
      {/* Score Result Modal */}
      {pendingScore && (
        <ScoreModal
          scoreData={pendingScore}
          gameType={gameType}
          onContinue={handleScoreModalContinue}
        />
      )}
      <div className="flex items-center space-x-2 sm:space-x-3 mb-3 sm:mb-4">
        <div className="w-8 h-8 sm:w-10 sm:h-10 bg-green-500/10 rounded-full flex items-center justify-center flex-shrink-0">
          <Icon name="Gamepad2" size={16} className="text-green-500 sm:w-5 sm:h-5" />
        </div>
        <div>
          <h2 className="text-lg sm:text-2xl font-bold text-foreground">{title}</h2>
          {description && <p className="text-xs sm:text-sm text-muted-foreground">{description}</p>}
        </div>
      </div>
      {/* Image preloading spinner — shown only when there are images to load */}
      {imagesLoading && imagePathsToResolve?.length > 0 && (
        <div className="flex flex-col items-center justify-center py-10 gap-3">
          <div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin" />
          <p className="text-sm text-muted-foreground">Loading game images…</p>
        </div>
      )}
      {(!imagesLoading || imagePathsToResolve?.length === 0) && gameType === 'memory-match' && (
        <div className="space-y-3 sm:space-y-4">
          <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 sm:gap-3">
            {shuffledCards?.map((card, index) => {
              const isFlipped = gameState?.flippedCards?.includes(index);
              const isMatched = gameState?.matchedPairs?.includes(card?.pairId);
              const resolvedImageUrl = card?.imagePath ? getDisplayUrl(card?.imagePath) : null;
              const hasImage = !!card?.imagePath;
              return (
                <button
                  key={index}
                  onClick={() => !isMatched && handleCardFlip(index)}
                  disabled={isMatched}
                  className={`${
                    hasImage ? 'aspect-square' : 'aspect-square'
                  } p-2 sm:p-3 rounded-lg border-2 transition-all flex items-center justify-center ${
                    isMatched
                      ? 'bg-green-100 border-green-500 cursor-not-allowed'
                      : isFlipped
                      ? 'bg-primary/10 border-primary' :'bg-card border-border hover:border-primary'
                  }`}
                  style={{ minHeight: 80, minWidth: 80 }}
                >
                  {isFlipped || isMatched ? (
                    <GameCardContent
                      value={card?.value}
                      imageUrl={resolvedImageUrl}
                      altText={card?.value}
                      className="w-full h-full"
                    />
                  ) : (
                    <span className="text-xl font-bold text-muted-foreground">?</span>
                  )}
                </button>
              );
            })}
          </div>
          {gameState?.matchedPairs?.length > 0 && gameState?.matchedPairs?.length === (gameData?.pairs?.length || 0) && (
            <div className="text-center text-success font-medium text-sm sm:text-base">
              <Icon name="CheckCircle2" size={18} className="inline mr-2" />
              All pairs matched! Great job!
            </div>
          )}
        </div>
      )}
      {(!imagesLoading || imagePathsToResolve?.length === 0) && gameType === 'word-search' && wordSearchGrid && (
        <div className="space-y-3 sm:space-y-4">
          <div className="bg-muted/30 p-3 sm:p-4 rounded-lg">
            <h3 className="text-sm sm:text-base font-medium text-foreground mb-2">Words to Find:</h3>
            <div className="flex flex-wrap gap-1.5 sm:gap-2">
              {gameData?.words?.map((word, index) => (
                <span
                  key={index}
                  className={`px-2 sm:px-3 py-0.5 sm:py-1 rounded-full text-xs sm:text-sm ${
                    gameState?.foundWords?.includes(word?.toUpperCase())
                      ? 'bg-green-100 text-green-700 line-through' :'bg-card border border-border text-foreground'
                  }`}
                >
                  {word}
                </span>
              ))}
            </div>
          </div>
          <div className="bg-white rounded-xl p-2 sm:p-3 border-2 border-border overflow-x-auto">
            <div className="grid gap-0.5 mx-auto w-fit" style={{ gridTemplateColumns: `repeat(${wordSearchGrid?.grid?.[0]?.length}, 1fr)` }}>
              {wordSearchGrid?.grid?.map((row, rowIndex) =>
                row?.map((letter, colIndex) => {
                  const cellKey = `${rowIndex}-${colIndex}`;
                  const isSelected = gameState?.currentSelection?.some(cell => cell?.key === cellKey);
                  const isFound = gameState?.selectedCells?.includes(cellKey);
                  return (
                    <button
                      key={cellKey}
                      onClick={() => handleWordSearchCellClick(rowIndex, colIndex)}
                      className={`w-7 h-7 flex items-center justify-center text-xs font-semibold rounded transition-all ${
                        isFound
                          ? 'bg-green-200 text-green-800'
                          : isSelected
                          ? 'bg-primary text-white' :'bg-gray-100 hover:bg-gray-200 text-gray-700'
                      }`}
                    >
                      {letter}
                    </button>
                  );
                })
              )}
            </div>
          </div>
          {gameState?.currentSelection?.length > 0 && (
            <div className="flex justify-center gap-2">
              <Button onClick={checkWordSearchSelection} iconName="Check" size="sm">Check Word</Button>
              <Button
                onClick={() => setGameState(prev => ({ ...prev, currentSelection: [] }))}
                variant="outline"
                iconName="X"
                size="sm"
              >
                Clear
              </Button>
            </div>
          )}
          {gameState?.foundWords?.length === gameData?.words?.length && (
            <div className="text-center text-success font-medium text-sm sm:text-base">
              <Icon name="CheckCircle2" size={18} className="inline mr-2" />
              All words found! Excellent!
            </div>
          )}
        </div>
      )}
      {(!imagesLoading || imagePathsToResolve?.length === 0) && gameType === 'drag-drop' && (
        <div className="space-y-3 sm:space-y-4">
          <p className="text-xs sm:text-sm text-muted-foreground">Drag items to arrange them in the correct order:</p>
          <div className="space-y-2">
            {dragDropOrder?.map((item, index) => {
              const hasImage = item?.itemType === 'image' && item?.itemImage;
              return (
                <div
                  key={index}
                  draggable
                  onDragStart={(e) => handleDragStart(e, index)}
                  onDragOver={handleDragOver}
                  onDrop={(e) => handleDrop(e, index)}
                  className={`p-3 sm:p-4 bg-card border-2 rounded-lg cursor-move hover:border-primary transition-all ${
                    draggedItem === index ? 'opacity-50 border-primary' : 'border-border'
                  }`}
                  style={{ minHeight: hasImage ? 100 : undefined }}
                >
                  <div className="flex items-center gap-3">
                    <Icon name="GripVertical" size={18} className="text-muted-foreground flex-shrink-0" />
                    {hasImage ? (
                      <div className="flex items-center gap-3 flex-1">
                        <img
                          src={getDisplayUrl(item?.itemImage)}
                          alt={item?.text || 'Drag item image'}
                          className="w-20 h-20 object-cover rounded-lg border border-border flex-shrink-0"
                          draggable={false}
                        />
                        {item?.text && (
                          <span className="text-sm text-muted-foreground">{item?.text}</span>
                        )}
                      </div>
                    ) : (
                      <span className="text-sm sm:text-base text-foreground">{item?.text}</span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
          <Button onClick={checkDragDropOrder} iconName="Check" fullWidth>Check Order</Button>
          {gameState?.orderChecked && (
            <div className={`text-center font-medium text-sm sm:text-base ${
              gameState?.isComplete ? 'text-success' : 'text-destructive'
            }`}>
              <Icon name={gameState?.isComplete ? 'CheckCircle2' : 'XCircle'} size={18} className="inline mr-2" />
              {gameState?.isComplete ? 'Perfect order!' : 'Not quite right. Try again!'}
            </div>
          )}
        </div>
      )}
      {gameType === 'true-false' && (
        <div className="space-y-3 sm:space-y-4">
          <div className="p-4 sm:p-6 bg-muted/30 rounded-lg">
            <p className="text-base sm:text-lg text-foreground text-center">{gameData?.statement}</p>
          </div>
          <div className="grid grid-cols-2 gap-3 sm:gap-4">
            <Button
              variant={gameState?.selectedAnswers?.trueFalse === 'true' ? 'default' : 'outline'}
              onClick={() => handleTrueFalseAnswer('true')}
              iconName="Check"
              fullWidth
            >
              True
            </Button>
            <Button
              variant={gameState?.selectedAnswers?.trueFalse === 'false' ? 'default' : 'outline'}
              onClick={() => handleTrueFalseAnswer('false')}
              iconName="X"
              fullWidth
            >
              False
            </Button>
          </div>
          {gameState?.selectedAnswers?.trueFalse && (
            <div className={`text-center font-medium text-sm sm:text-base ${
              gameState?.isComplete ? 'text-success' : 'text-destructive'
            }`}>
              <Icon name={gameState?.isComplete ? 'CheckCircle2' : 'XCircle'} size={18} className="inline mr-2" />
              {gameState?.isComplete ? 'Correct!' : 'Incorrect. Try again!'}
            </div>
          )}
        </div>
      )}
      {gameType === 'fill-blanks' && (
        <div className="space-y-3 sm:space-y-4">
          {gameData?.blanks?.map((blank, index) => (
            <div key={index} className="p-3 sm:p-4 bg-muted/30 rounded-lg space-y-3">
              <p className="text-sm sm:text-base text-foreground">
                {blank?.sentence?.split('_____')?.map((part, i, arr) => (
                  <React.Fragment key={i}>
                    {part}
                    {i < arr?.length - 1 && (
                      <input
                        type="text"
                        value={gameState?.userInput?.[index] || ''}
                        onChange={(e) => handleFillBlankInput(index, e?.target?.value)}
                        className="inline-block w-24 sm:w-32 px-2 py-1 mx-1 border-b-2 border-primary bg-transparent text-center focus:outline-none text-sm sm:text-base"
                        placeholder="?"
                      />
                    )}
                  </React.Fragment>
                ))}
              </p>
            </div>
          ))}
          <Button onClick={checkFillBlanks} iconName="Check" fullWidth>Check Answers</Button>
          {gameState?.isComplete !== undefined && gameState?.isComplete !== false && (
            <div className={`text-center font-medium text-sm sm:text-base ${
              gameState?.isComplete ? 'text-success' : 'text-destructive'
            }`}>
              <Icon name={gameState?.isComplete ? 'CheckCircle2' : 'XCircle'} size={18} className="inline mr-2" />
              {gameState?.isComplete ? 'All correct!' : 'Some answers are incorrect. Try again!'}
            </div>
          )}
        </div>
      )}
      {gameType === 'sorting' && (
        <div className="space-y-3 sm:space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
            {gameData?.categories?.map((category, catIndex) => (
              <div key={catIndex} className="p-3 sm:p-4 bg-muted/30 rounded-lg">
                <h3 className="text-sm sm:text-base font-medium text-foreground mb-2 sm:mb-3">{category?.name}</h3>
                <div
                  className={`space-y-2 min-h-[80px] sm:min-h-[100px] border-2 border-dashed rounded-lg p-2 transition-colors ${
                    dragOverCategory === catIndex
                      ? 'border-primary bg-primary/10' :'border-border'
                  }`}
                  onDragOver={(e) => handleSortingDragOver(e, catIndex)}
                  onDragLeave={handleSortingDragLeave}
                  onDrop={(e) => handleSortingDropOnCategory(e, catIndex)}
                >
                  {Object.entries(gameState?.categoryAssignments || {})
                    ?.filter(([_, assignedCat]) => assignedCat === catIndex)
                    ?.map(([itemKey]) => {
                      const allItems = gameData?.categories?.flatMap(cat => cat?.items || []);
                      const itemObj = allItems?.find(it =>
                        (typeof it === 'object' ? (it?.text || JSON.stringify(it)) : it) === itemKey
                      );
                      const hasImage = itemObj && typeof itemObj === 'object' && itemObj?.itemType === 'image' && itemObj?.itemImage;
                      return (
                        <div
                          key={itemKey}
                          draggable={true}
                          onDragStart={(e) => { e?.stopPropagation(); handleSortingDragStart(e, itemKey, catIndex); }}
                          onDragEnd={handleSortingDragEnd}
                          className={`p-2 bg-card border border-border rounded cursor-grab active:cursor-grabbing hover:border-primary transition-all select-none ${
                            sortingDraggedItem?.item === itemKey ? 'opacity-50' : ''
                          }`}
                          style={{ minHeight: hasImage ? 90 : undefined }}
                        >
                          {hasImage ? (
                            <div className="flex items-center gap-2 pointer-events-none">
                              <span className="text-muted-foreground text-xs flex-shrink-0">⠿</span>
                              <img
                                src={getDisplayUrl(itemObj?.itemImage)}
                                alt={itemObj?.text || 'Sort item image'}
                                className="w-16 h-16 object-cover rounded border border-border flex-shrink-0"
                                draggable={false}
                              />
                              {itemObj?.text && (
                                <span className="text-xs text-muted-foreground">{itemObj?.text}</span>
                              )}
                            </div>
                          ) : (
                            <div className="flex items-center gap-1.5 pointer-events-none">
                              <span className="text-muted-foreground text-xs">⠿</span>
                              {itemKey}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  {dragOverCategory === catIndex && (
                    <div className="p-2 border-2 border-dashed border-primary/50 rounded text-xs text-primary/60 text-center">
                      Drop here
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
          <div
            className="p-3 sm:p-4 bg-card border border-border rounded-lg"
            onDragOver={(e) => { e?.preventDefault(); setDragOverCategory('pool'); }}
            onDragLeave={(e) => { if (!e?.currentTarget?.contains(e?.relatedTarget)) setDragOverCategory(null); }}
            onDrop={handleSortingDropOnPool}
          >
            <h3 className="text-sm sm:text-base font-medium text-foreground mb-2 sm:mb-3">Items to Sort:</h3>
            <div className="flex flex-wrap gap-1.5 sm:gap-2 min-h-[40px]">
              {gameData?.categories?.flatMap(cat => cat?.items || [])
                ?.map(it => ({
                  obj: it,
                  key: typeof it === 'object' ? (it?.text || JSON.stringify(it)) : it
                }))
                ?.filter(({ key }) => gameState?.categoryAssignments?.[key] === undefined || gameState?.categoryAssignments?.[key] === null)
                ?.map(({ obj, key }, index) => {
                  const hasImage = obj && typeof obj === 'object' && obj?.itemType === 'image' && obj?.itemImage;
                  return (
                    <div
                      key={key}
                      draggable={true}
                      onDragStart={(e) => { e?.stopPropagation(); handleSortingDragStart(e, key, null); }}
                      onDragEnd={handleSortingDragEnd}
                      className={`${
                        hasImage
                          ? 'p-1.5 bg-primary/10 border border-primary rounded-lg cursor-grab active:cursor-grabbing hover:bg-primary/20 transition-all select-none' :'px-2 sm:px-3 py-1.5 sm:py-2 bg-primary/10 border border-primary rounded-lg text-xs sm:text-sm cursor-grab active:cursor-grabbing hover:bg-primary/20 transition-all select-none'
                      } ${
                        sortingDraggedItem?.item === key ? 'opacity-50' : ''
                      }`}
                      style={{ minHeight: hasImage ? 90 : undefined, minWidth: hasImage ? 90 : undefined }}
                    >
                      {hasImage ? (
                        <div className="flex flex-col items-center gap-1 pointer-events-none">
                          <img
                            src={getDisplayUrl(obj?.itemImage)}
                            alt={obj?.text || 'Sort item image'}
                            className="w-20 h-20 object-cover rounded"
                            draggable={false}
                          />
                          {obj?.text && (
                            <span className="text-xs text-center text-muted-foreground">{obj?.text}</span>
                          )}
                        </div>
                      ) : (
                        <span className="pointer-events-none">{key}</span>
                      )}
                    </div>
                  );
                })}
              {gameData?.categories?.flatMap(cat => cat?.items || [])
                ?.map(it => typeof it === 'object' ? (it?.text || JSON.stringify(it)) : it)
                ?.filter(key => gameState?.categoryAssignments?.[key] === undefined || gameState?.categoryAssignments?.[key] === null)
                ?.length === 0 && (
                <p className="text-xs text-muted-foreground italic">All items have been sorted</p>
              )}
            </div>
          </div>
          <Button onClick={checkSorting} iconName="Check" fullWidth>Check Sorting</Button>
          {(gameState?.isComplete === false || gameState?.isComplete === true) && (
            <div className={`text-center font-medium text-sm sm:text-base ${
              gameState?.isComplete ? 'text-success' : 'text-destructive'
            }`}>
              <Icon name={gameState?.isComplete ? 'CheckCircle2' : 'XCircle'} size={18} className="inline mr-2" />
              {gameState?.isComplete 
                ? 'Perfect sorting!' 
                : gameState?.notAllPlaced 
                  ? 'Please sort all items into categories first!' :'Some items are in wrong categories. Try again!'}
            </div>
          )}
        </div>
      )}
      {gameType === 'image-hotspot' && (
        <ImageHotspotPlayer
          gameData={gameData}
          onComplete={(scoreData) => showScoreAndComplete(scoreData)}
        />
      )}
      {!gameType && (
        <div className="bg-gradient-to-br from-green-50 to-blue-50 rounded-xl p-8 sm:p-12 text-center">
          <Icon name="Gamepad2" size={48} className="text-primary mx-auto mb-4" />
          <p className="text-sm sm:text-base text-muted-foreground mb-4">{description || 'Interactive game component'}</p>
          <div className="inline-flex items-center space-x-2 text-xs sm:text-sm text-primary">
            <Icon name="Sparkles" size={14} />
            <span>Game will load here</span>
          </div>
        </div>
      )}
      {!isCompleted && gameType && (
        <Button onClick={() => onComplete?.()} iconName="Check" fullWidth>Mark as Completed</Button>
      )}
      {isCompleted && (
        <div className="flex items-center justify-center space-x-2 text-success">
          <Icon name="CheckCircle2" size={18} />
          <span className="text-xs sm:text-sm font-medium">Completed</span>
        </div>
      )}
    </div>
  );
};

export default GameRenderer;
